import React, { useState, useEffect, useRef } from "react";
import {
  Menu,
  ChevronDown,
  User,
  LogOut,
  Settings,
  MessageSquare,
  Check,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePhoneAccount } from "../../contexts/PhoneAccountContext";
import SubscriptionStatus from "../SubscriptionStatus";

const EMBEDDED_SIGNUP_CONFIG_ID = "1404119364652323";
const REDIRECT_URI = "https://90c56c829ca4.ngrok-free.app/whatsapp/callback";

const Header = ({ toggleSidebar }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showWabaDropdown, setShowWabaDropdown] = useState(false);
  const [userName, setUserName] = useState("User");
  const [userInitial, setUserInitial] = useState("U");
  const [pageTitle, setPageTitle] = useState("Dashboard");
  const [pageSubtitle, setPageSubtitle] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  const [selectedWaba, setSelectedWaba] = useState(null);
  const dropdownRef = useRef(null);
  const wabaDropdownRef = useRef(null);

  // Get phone accounts and selected phoneId from PhoneAccountContext
  const { 
    phoneAccounts: wabaAccounts, 
    selectedPhoneId: contextSelectedPhoneId,
    selectedPhoneAccount: contextSelectedAccount,
    loading: loadingWabaAccounts,
    setSelectedPhoneId: setContextPhoneId,
    fetchPhoneAccounts: fetchContextPhoneAccounts
  } = usePhoneAccount();

  const location = useLocation();
  const navigate = useNavigate();

  // Update page title
  useEffect(() => {
    const path = location.pathname;
    if (path.includes("dashboard")) {
      setPageTitle("Dashboard");
      setPageSubtitle("Overview of your messaging analytics");
    } else if (path.includes("waba")) {
      setPageTitle("Whatsapp Numbers");
      setPageSubtitle("Manage your WhatsApp Numbers");
    } else if (path.includes("templates")) {
      setPageTitle("Templates");
      setPageSubtitle("Create and manage your WhatsApp message templates");
    } else if (path.includes("broadcast")) {
      setPageTitle("Campaigns");
      setPageSubtitle("Manage and track your WhatsApp campaigns");
    } else if (path.includes("flow-list")) {
      setPageTitle("Flow Lists");
      setPageSubtitle("Manage your automation flows");
    } else if (path.includes("contact-lists")) {
      setPageTitle("Contact Lists");
      setPageSubtitle("Create and manage your contact lists for campaigns");
    } else if (path.includes("live-chats")) {
      setPageTitle("Live Chats");
      setPageSubtitle("Manage real-time conversations with your customers");
    } else if (path.includes("download-center")) {
      setPageTitle("Download Center");
      setPageSubtitle("View and download your campaign log files");
    } else if (path.includes("profile")) {
      setPageTitle("Your Profile");
      setPageSubtitle("Manage your account settings");
    } else if (path.includes("settings")) {
      setPageTitle("Settings");
      setPageSubtitle("Configure your preferences");
    } else {
      setPageTitle("Dashboard");
      setPageSubtitle("");
    }
  }, [location]);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        if (parsedUser.name) {
          setUserName(parsedUser.name);
          setUserInitial(parsedUser.name.charAt(0).toUpperCase());
        }
      } catch (e) {
        console.error("Error parsing user data:", e);
      }
    }
  }, []);

  const toggleUserMenu = () => {
    setShowUserMenu((v) => !v);
    setShowWabaDropdown(false);
  };

  const toggleWabaDropdown = () => {
    setShowWabaDropdown((v) => !v);
    setShowUserMenu(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("userAccessToken");
    localStorage.removeItem("user_id");
    window.location.href = "/login";
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    window.location.reload();
    setTimeout(() => setIsRefreshing(false), 2000);
  };

  // Update WhatsApp connection status based on phone accounts from context
  useEffect(() => {
    setWhatsappConnected(wabaAccounts.length > 0);
    // Update selectedWaba for UI display
    if (contextSelectedAccount) {
      setSelectedWaba(contextSelectedAccount);
    } else if (wabaAccounts.length > 0 && !contextSelectedPhoneId) {
      // Context will auto-select, but update UI immediately
      setSelectedWaba(wabaAccounts[0]);
    } else if (wabaAccounts.length === 0) {
      setSelectedWaba(null);
    }
  }, [wabaAccounts, contextSelectedPhoneId, contextSelectedAccount]);

  const checkWhatsappConnection = async () => {
    // Refresh phone accounts from context
    if (fetchContextPhoneAccounts) {
      await fetchContextPhoneAccounts();
    }
  };

  const handleWabaChange = (account) => {
    setSelectedWaba(account);
    // Update context with new phoneId (this is the only source of truth)
    setContextPhoneId(account.phoneId);
    // Dispatch event for backward compatibility with other components
    window.dispatchEvent(
      new CustomEvent("wabaAccountChanged", { detail: account })
    );
    setShowWabaDropdown(false);
  };

  const sendCodeToBackend = async (code) => {
    const token = localStorage.getItem("userAccessToken");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/facebook/embedded-signup/callback`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ code }),
          credentials: "include",
        }
      );
      const data = await res.json();
      console.log("Backend response:", data);
      setWhatsappLoading(false);
      // Refresh context phone accounts
      if (fetchContextPhoneAccounts) {
        await fetchContextPhoneAccounts();
      }
      window.dispatchEvent(new CustomEvent("phoneAccountsUpdated"));
    } catch (error) {
      console.error("Failed to send signup code to backend:", error);
      setWhatsappLoading(false);
      alert("Failed to complete onboarding. Please try again.");
    }
  };

  const handleEmbeddedSignup = () => {
    setWhatsappLoading(true);

    const token = localStorage.getItem("userAccessToken");
    if (!token) {
      alert("⚠️ Please log in to your account first.");
      setWhatsappLoading(false);
      return;
    }

    if (window.FB && typeof window.FB.login === "function") {
      window.FB.login(
        (response) => {
          if (response.authResponse && response.authResponse.code) {
            const code = response.authResponse.code;
            console.log("FB login code received:", code);
            sendCodeToBackend(code);
          } else {
            console.warn(
              "No auth code received in FB login response",
              response
            );
            setWhatsappLoading(false);
            alert("Failed to get authorization code. Please try again.");
          }
        },
        {
          config_id: EMBEDDED_SIGNUP_CONFIG_ID,
          response_type: "code",
          override_default_response_type: true,
          redirect_uri: REDIRECT_URI,
        }
      );
    } else {
      setWhatsappLoading(false);
      alert("Facebook SDK not loaded. Please refresh and try again.");
    }
  };

  useEffect(() => {
    checkWhatsappConnection();

    const handleAccountUpdate = () => {
      if (fetchContextPhoneAccounts) {
        fetchContextPhoneAccounts();
      }
    };

    window.addEventListener("phoneAccountsUpdated", handleAccountUpdate);
    window.addEventListener("wabaAccountsUpdated", handleAccountUpdate);

    return () => {
      window.removeEventListener("phoneAccountsUpdated", handleAccountUpdate);
      window.removeEventListener("wabaAccountsUpdated", handleAccountUpdate);
    };
  }, [fetchContextPhoneAccounts]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (
        wabaDropdownRef.current &&
        !wabaDropdownRef.current.contains(event.target)
      ) {
        setShowWabaDropdown(false);
      }
    };
    if (showUserMenu || showWabaDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showUserMenu, showWabaDropdown]);

  return (
    <header className="fixed top-0 right-0 left-0 lg:left-[104px] z-30">
      {/* Header Container with Padding */}
      <div className="px-3 py-3 lg:pr-3">
        {/* Header Content */}
        <div className="rounded-[24px] h-14 md:h-16 px-3 md:px-5 flex items-center justify-between shadow-lg bg-gray-900 border border-gray-800">
          {/* Left: menu + title */}
          <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1 overflow-hidden">
            <button
              onClick={toggleSidebar}
              className="lg:hidden p-2 rounded-xl text-white/70 hover:text-white hover:bg-white/10 transition-all duration-200 flex-shrink-0"
              aria-label="Toggle sidebar"
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="min-w-0 flex-1 overflow-hidden">
              <h1 className="text-sm md:text-base lg:text-lg font-bold text-white truncate">
                {pageTitle}
              </h1>
              {pageSubtitle && (
                <p className="hidden sm:block text-[10px] md:text-xs text-white/50 truncate mt-0.5">
                  {pageSubtitle}
                </p>
              )}
            </div>
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
            {/* Subscription Status */}
            <SubscriptionStatus />
            
            {/* WhatsApp Connect Button */}
            {!whatsappConnected && (
              <button
                onClick={handleEmbeddedSignup}
                disabled={whatsappLoading}
                className="flex items-center justify-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 text-xs font-semibold text-gray-900 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: "#eefa94" }}
                title="Connect WhatsApp Business"
              >
                {whatsappLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-gray-900 border-t-transparent"></div>
                    <span className="hidden sm:inline">Connecting...</span>
                  </>
                ) : (
                  <>
                    <MessageSquare size={14} className="md:w-4 md:h-4" />
                    <span className="hidden sm:inline">Connect WhatsApp</span>
                  </>
                )}
              </button>
            )}

            {/* WABA Account Dropdown */}
            {loadingWabaAccounts ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10">
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white/30 border-t-white"></div>
                <span className="hidden md:inline text-xs text-white/70 font-medium">
                  Loading...
                </span>
              </div>
            ) : wabaAccounts.length > 0 ? (
              <div className="relative" ref={wabaDropdownRef}>
                <button
                  onClick={toggleWabaDropdown}
                  className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 text-xs font-semibold text-white border border-white/20 rounded-xl hover:bg-white/10 hover:border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200 max-w-[140px] sm:max-w-[180px] md:max-w-[240px] bg-white/5"
                  title={
                    selectedWaba
                      ? `${selectedWaba.phoneName} - ${selectedWaba.phoneNumber}`
                      : "Select WhatsApp Business Account"
                  }
                >
                  <MessageSquare size={14} className="flex-shrink-0" />
                  <span className="truncate text-xs">
                    {selectedWaba
                      ? `${selectedWaba.phoneName || "Account"}`
                      : "Select Account"}
                  </span>
                  <ChevronDown
                    size={14}
                    className={`flex-shrink-0 transition-transform duration-200 ${
                      showWabaDropdown ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Custom Dropdown Menu */}
                {showWabaDropdown && (
                  <div className="absolute right-0 mt-2 w-72 md:w-80 rounded-2xl border border-gray-700 shadow-2xl overflow-hidden z-50 bg-gray-900">
                    <div className="px-4 py-3 border-b border-gray-700 bg-gray-800">
                      <p className="text-xs font-bold text-white">
                        WhatsApp Business Accounts
                      </p>
                      <p className="text-[10px] text-white/60 mt-0.5 font-medium">
                        Select an account to use
                      </p>
                    </div>
                    <div className="max-h-[280px] overflow-y-auto">
                      <ul className="py-1">
                        {wabaAccounts.map((account) => {
                          const isSelected =
                            selectedWaba?.phoneId === account.phoneId;
                          return (
                            <li key={account.phoneId}>
                              <button
                                onClick={() => handleWabaChange(account)}
                                className={`w-full text-left px-4 py-3 transition-all duration-200 flex items-start gap-3 ${
                                  isSelected ? "" : "hover:bg-white/5"
                                }`}
                                style={{
                                  backgroundColor: isSelected
                                    ? "#eefa94"
                                    : "transparent",
                                  borderLeftWidth: isSelected ? "3px" : "0",
                                  borderLeftColor: isSelected
                                    ? "#000000"
                                    : "transparent",
                                }}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p
                                      className={`text-sm font-semibold truncate ${
                                        isSelected
                                          ? "text-gray-900"
                                          : "text-white"
                                      }`}
                                    >
                                      {account.phoneName || "Account"}
                                    </p>
                                  </div>
                                  <p
                                    className={`text-xs mt-1 truncate font-medium ${
                                      isSelected
                                        ? "text-gray-700"
                                        : "text-white/60"
                                    }`}
                                  >
                                    {account.phoneNumber}
                                  </p>
                                  <p
                                    className={`text-[10px] mt-0.5 truncate ${
                                      isSelected
                                        ? "text-gray-600"
                                        : "text-white/40"
                                    }`}
                                  >
                                    ID: {account.wabaId}
                                  </p>
                                </div>
                                {isSelected && (
                                  <Check
                                    size={18}
                                    className="text-gray-900 flex-shrink-0 mt-0.5"
                                    strokeWidth={2.5}
                                  />
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            ) : null}

            {/* User menu */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={toggleUserMenu}
                className="flex items-center gap-1.5 md:gap-2 rounded-xl bg-white/10 hover:bg-white/20 pl-1 md:pl-1.5 pr-2.5 md:pr-3 py-1.5 border border-white/20 transition-all duration-200"
                aria-haspopup="menu"
                aria-expanded={showUserMenu}
              >
                <div
                  className="w-7 h-7 md:w-8 md:h-8 rounded-full font-bold flex items-center justify-center text-xs md:text-sm flex-shrink-0 text-gray-900"
                  style={{ backgroundColor: "#eefa94" }}
                >
                  {userInitial}
                </div>
                <span className="hidden sm:block text-xs font-semibold text-white truncate max-w-16 md:max-w-24">
                  {userName}
                </span>
                <ChevronDown className="w-3.5 h-3.5 md:w-4 md:h-4 text-white/70 flex-shrink-0" />
              </button>

              {showUserMenu && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-52 md:w-56 rounded-2xl border border-gray-700 shadow-2xl overflow-hidden bg-gray-900"
                >
                  <div className="px-4 py-3 border-b border-gray-700 bg-gray-800">
                    <p className="text-sm font-bold text-white truncate">
                      {userName}
                    </p>
                    <p className="text-xs text-white/60 mt-0.5 font-medium">
                      Account User
                    </p>
                  </div>
                  <ul className="py-1">
                    <li>
                      <button
                        onClick={() => {
                          navigate("/profile");
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 transition-all duration-200"
                        role="menuitem"
                      >
                        <User className="w-4 h-4" />
                        Your Profile
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => {
                          navigate("/settings");
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 transition-all duration-200"
                        role="menuitem"
                      >
                        <Settings className="w-4 h-4" />
                        Settings
                      </button>
                    </li>
                    <li className="border-t border-gray-700 mt-1">
                      <button
                        onClick={handleLogout}
                        className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all duration-200"
                        role="menuitem"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
